/**
 * CardOnFileHealthWidget — sits on the Coach Accounting page.
 *
 * Owner spec 2026-02-17: keeping a card on file MUST always work. This
 * widget is the always-on signal that the pipeline is healthy.
 *
 * Surfaces:
 *   - 3 KPI tiles: With card · Suspect drift · No Stripe customer
 *   - "Last full sweep ran X · fixed N parents" status line
 *   - "Run full sweep now" button → hits the sweep, refreshes KPIs
 *
 * Background: a daily job already auto-sweeps every ~24h, so drift can
 * never persist longer than that even without owner intervention. This
 * widget gives the owner a manual trigger for "fix it right now" + a
 * heads-up tile when the suspect count is non-zero.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, RefreshCw, CreditCard, CheckCircle2, AlertTriangle, ChevronDown, ChevronRight, Webhook } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtRelative = (iso) => {
    if (!iso) return "never";
    try {
        const d = new Date(iso);
        const mins = Math.round((Date.now() - d.getTime()) / 60000);
        if (mins < 1) return "just now";
        if (mins < 60) return `${mins}m ago`;
        const hrs = Math.round(mins / 60);
        if (hrs < 24) return `${hrs}h ago`;
        const days = Math.round(hrs / 24);
        return `${days}d ago`;
    } catch {
        return iso;
    }
};

function Kpi({ label, value, hint, tone = "default", testid }) {
    const toneClasses = {
        default: "border-zinc-800 bg-zinc-950",
        amber: "border-amber-900/60 bg-amber-950/30",
        red: "border-red-900/60 bg-red-950/30",
        green: "border-emerald-900/60 bg-emerald-950/20",
    };
    return (
        <div
            className={`border ${toneClasses[tone] || toneClasses.default} rounded-lg p-4`}
            data-testid={testid}
        >
            <div className="text-[10px] tracking-[0.18em] uppercase text-zinc-500 mb-2">
                {label}
            </div>
            <div className="text-2xl font-semibold text-white font-mono">{value}</div>
            {hint && <div className="text-[11px] text-zinc-500 mt-1">{hint}</div>}
        </div>
    );
}

export default function CardOnFileHealthWidget() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sweeping, setSweeping] = useState(false);
    const [error, setError] = useState(null);
    const [diag, setDiag] = useState(null);
    const [diagOpen, setDiagOpen] = useState(false);
    const [diagLoading, setDiagLoading] = useState(false);

    const loadDiag = async () => {
        setDiagLoading(true);
        try {
            const r = await axios.get(`${API}/coach/billing/card-add-diagnostic`, {
                withCredentials: true,
            });
            setDiag(r.data);
        } catch (e) {
            toast.error("Couldn't load diagnostic", { description: e?.response?.data?.detail });
        } finally {
            setDiagLoading(false);
        }
    };

    const toggleDiag = () => {
        const next = !diagOpen;
        setDiagOpen(next);
        if (next && !diag) loadDiag();
    };

    const load = async () => {
        setError(null);
        try {
            const r = await axios.get(`${API}/coach/billing/card-on-file-health`, {
                withCredentials: true,
            });
            setData(r.data);
        } catch (e) {
            setError(e?.response?.data?.detail || "Failed to load card-on-file health");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const runSweep = async () => {
        // Confirm only when we'd actually touch real users — silent if nothing
        // to do (suspects==0).
        const suspectCount = data?.customer_no_card ?? 0;
        const intro = suspectCount > 0
            ? `${suspectCount} client${suspectCount === 1 ? "" : "s"} may be missing card-on-file. Run a full sweep against Stripe now?`
            : "Re-sync every client's card-on-file state with Stripe? Safe — it's a no-op for clients already in good shape.";
        if (!window.confirm(intro)) return;
        setSweeping(true);
        try {
            const r = await axios.post(
                `${API}/coach/billing/sync-all-card-on-file`,
                {}, { withCredentials: true },
            );
            const c = r.data?.counts || {};
            const fixed = (c.promoted || 0) + (c.cache_refreshed || 0);
            if (fixed > 0) {
                toast.success(`Sweep complete — fixed ${fixed} client${fixed === 1 ? "" : "s"}`);
            } else {
                toast.success("Sweep complete — everything was already in good shape.");
            }
            await load();
        } catch (e) {
            toast.error("Sweep failed", { description: e?.response?.data?.detail || "Server error" });
        } finally {
            setSweeping(false);
        }
    };

    if (loading) {
        return (
            <div
                className="border border-zinc-800 bg-zinc-950 rounded-lg p-8 flex items-center gap-3 text-zinc-400"
                data-testid="card-health-loading"
            >
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Loading card-on-file health…</span>
            </div>
        );
    }

    if (error) {
        return (
            <div
                className="border border-red-900/60 bg-red-950/30 rounded-lg p-4 text-red-200 text-sm"
                data-testid="card-health-error"
            >
                {error}
            </div>
        );
    }
    if (!data) return null;

    const suspectTone = data.customer_no_card > 0 ? "amber" : "default";

    return (
        <section data-testid="card-on-file-health-widget">
            <div className="flex items-end justify-between mb-3 gap-3 flex-wrap">
                <div>
                    <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                        <CreditCard className="w-4 h-4 text-emerald-400" />
                        Card-on-File Health
                    </h3>
                    <p className="text-[11px] text-zinc-500 mt-0.5">
                        Auto-syncs daily. Suspect drift = client has a Stripe customer
                        but the card-on-file flag is off (the symptom of the 2026-02-17 webhook bug).
                    </p>
                </div>
                <button
                    onClick={runSweep}
                    disabled={sweeping}
                    className={`flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase px-3 py-2 border transition-colors disabled:opacity-40 ${data.customer_no_card > 0 ? "border-amber-500 text-amber-200 hover:bg-amber-950" : "border-zinc-700 text-zinc-300 hover:text-white"}`}
                    data-testid="card-health-run-sweep"
                >
                    {sweeping ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                    {sweeping ? "Sweeping…" : "Run full sweep now"}
                </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
                <Kpi
                    label="With card on file"
                    value={data.with_card}
                    hint="Ready for one-tap charge"
                    tone="green"
                    testid="kpi-with-card"
                />
                <Kpi
                    label="Suspect drift"
                    value={data.customer_no_card}
                    hint={data.customer_no_card > 0
                        ? "Has Stripe customer · cache off"
                        : "All clear"}
                    tone={suspectTone}
                    testid="kpi-suspect-drift"
                />
                <Kpi
                    label="No Stripe customer"
                    value={data.no_stripe_customer}
                    hint="Never paid yet — expected"
                    testid="kpi-no-customer"
                />
            </div>

            {/* Last sweep status */}
            <div
                className="text-[11px] text-zinc-500 border border-zinc-900 bg-zinc-950 rounded px-3 py-2 flex items-center justify-between gap-3"
                data-testid="card-health-last-sweep"
            >
                <div className="flex items-center gap-2 min-w-0 truncate">
                    {data.last_sweep_at ? (
                        <CheckCircle2 className="w-3 h-3 text-emerald-400 shrink-0" />
                    ) : (
                        <AlertTriangle className="w-3 h-3 text-amber-400 shrink-0" />
                    )}
                    <span className="truncate">
                        Last full sweep: <b className="text-zinc-300">{fmtRelative(data.last_sweep_at)}</b>
                        {data.last_sweep_fixed_count > 0 && (
                            <> · fixed <b className="text-emerald-300">{data.last_sweep_fixed_count}</b> client{data.last_sweep_fixed_count === 1 ? "" : "s"}</>
                        )}
                    </span>
                </div>
                {data.last_sweep_counts && (
                    <span className="text-[10px] text-zinc-600 font-mono shrink-0">
                        checked {data.last_sweep_counts.checked} · promoted {data.last_sweep_counts.promoted} · errors {data.last_sweep_counts.error}
                    </span>
                )}
            </div>

            {/* Diagnostic disclosure — per-session view of recent card-add attempts */}
            <div className="mt-3 border border-zinc-900 bg-zinc-950 rounded">
                <button
                    type="button"
                    data-testid="card-diag-toggle"
                    onClick={toggleDiag}
                    className="w-full px-3 py-2 text-left flex items-center justify-between hover:bg-zinc-900/40"
                >
                    <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-300 inline-flex items-center gap-1.5">
                        <Webhook className="w-3 h-3 text-brand-red" />
                        Diagnostic — last 20 card-add attempts
                    </span>
                    {diagOpen ? <ChevronDown className="w-3.5 h-3.5 text-zinc-400" /> : <ChevronRight className="w-3.5 h-3.5 text-zinc-400" />}
                </button>
                {diagOpen && (
                    <div className="border-t border-zinc-900 p-3" data-testid="card-diag-panel">
                        {diagLoading && (
                            <div className="text-xs text-zinc-400 flex items-center gap-2">
                                <Loader2 className="w-3 h-3 animate-spin" /> Loading…
                            </div>
                        )}
                        {diag && (
                            <>
                                <div className="flex items-center gap-3 mb-3 text-[10px] tracking-[0.18em] uppercase">
                                    {diag.webhook_secret_configured ? (
                                        <span className="text-emerald-400 inline-flex items-center gap-1">
                                            <CheckCircle2 className="w-3 h-3" /> Webhook secret configured
                                        </span>
                                    ) : (
                                        <span className="text-red-400 inline-flex items-center gap-1">
                                            <AlertTriangle className="w-3 h-3" /> Webhook secret MISSING
                                        </span>
                                    )}
                                    <span className="text-zinc-500">
                                        {diag.recent_webhooks.length} webhook event{diag.recent_webhooks.length === 1 ? "" : "s"} received recently
                                    </span>
                                    <button
                                        type="button"
                                        onClick={loadDiag}
                                        className="text-zinc-300 hover:text-brand-red ml-auto"
                                    >
                                        <RefreshCw className={`w-3 h-3 ${diagLoading ? "animate-spin" : ""}`} />
                                    </button>
                                </div>
                                {diag.sessions.length === 0 ? (
                                    <div className="text-xs text-zinc-500 py-3 text-center">
                                        No card-add attempts recorded yet. Have a parent click &quot;Add card on file&quot; — then refresh.
                                    </div>
                                ) : (
                                    <table className="w-full text-[11px]">
                                        <thead className="text-[9px] tracking-[0.18em] uppercase text-zinc-500">
                                            <tr className="border-b border-zinc-900">
                                                <th className="px-2 py-1.5 text-left">Parent</th>
                                                <th className="px-2 py-1.5 text-left">Started</th>
                                                <th className="px-2 py-1.5 text-left">Flow</th>
                                                <th className="px-2 py-1.5 text-left">Webhook</th>
                                                <th className="px-2 py-1.5 text-left">Card now</th>
                                                <th className="px-2 py-1.5 text-left">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {diag.sessions.map((s) => {
                                                const tone = s.health === "ok"
                                                    ? "text-emerald-400"
                                                    : s.health === "no_webhook"
                                                        ? "text-red-400"
                                                        : "text-amber-400";
                                                const label = s.health === "ok"
                                                    ? "OK"
                                                    : s.health === "no_webhook"
                                                        ? "No webhook"
                                                        : "Webhook · no card";
                                                return (
                                                    <tr key={s.session_id} className="border-b border-zinc-900 last:border-b-0">
                                                        <td className="px-2 py-1.5 text-white truncate max-w-[180px]">
                                                            {s.user_name || s.user_id}
                                                        </td>
                                                        <td className="px-2 py-1.5 text-zinc-400 text-[10px]">
                                                            {s.created_at ? new Date(s.created_at).toLocaleString() : "—"}
                                                        </td>
                                                        <td className="px-2 py-1.5 text-zinc-300 text-[10px]">
                                                            {s.initiated_by || s.kind}
                                                        </td>
                                                        <td className="px-2 py-1.5 text-zinc-400 text-[10px]">
                                                            {s.webhook_received_at ? new Date(s.webhook_received_at).toLocaleString() : <span className="text-red-400">—</span>}
                                                        </td>
                                                        <td className="px-2 py-1.5 text-[10px]">
                                                            {s.card_on_file_now ? <span className="text-emerald-400">YES</span> : <span className="text-red-400">no</span>}
                                                        </td>
                                                        <td className={`px-2 py-1.5 text-[10px] font-semibold ${tone}`}>
                                                            {label}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>
        </section>
    );
}
